import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { axisProps, chartColors, formatMonth } from '@shared/lib/chart-theme';
import { ChartTooltip } from '@features/analytics/ui/ChartTooltip';
import { formatMoney, type MonthlyAmount } from '@features/analytics/model/dashboard.types';

/**
 * The headline growth chart: recurring revenue over time, one series. One series
 * means no legend — the card title names it.
 */
export function MrrChart({ data, currency }: { data: MonthlyAmount[]; currency: string }) {
  const points = data.map((point) => ({
    month: formatMonth(point.month),
    MRR: point.amount,
  }));

  return (
    <ResponsiveContainer width="100%" height={172}>
      <AreaChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="mrr-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={chartColors.primary} stopOpacity={0.22} />
            <stop offset="100%" stopColor={chartColors.primary} stopOpacity={0.01} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis
          {...axisProps}
          width={52}
          tickFormatter={(value: number) => formatMoney(value, currency)}
        />

        <Tooltip
          content={<ChartTooltip formatValue={(value) => formatMoney(value, currency)} />}
          cursor={{ stroke: chartColors.axis, strokeWidth: 1 }}
        />

        <Area
          type="monotone"
          dataKey="MRR"
          stroke={chartColors.primary}
          strokeWidth={2}
          fill="url(#mrr-fill)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: chartColors.surface }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
